
import { useCallback, startTransition, useRef } from 'react';
import { Email } from '../../types';
import { fetchEmailsInFolder, fetchEmailById, fetchLegacyEmailMessage } from '@/services/api/email';
import { emailCacheManager } from './emailCacheManager';
import { transformFolderEmailDetailToEmail, transformFolderEmailToEmail, transformMessageDetailToEmail } from '@/services/email/utils/emailTransformers';

interface UseEmailFetchOperationsProps {
  emails: Email[];
  setEmails: (emails: Email[]) => void;
  setCurrentEmail: (email: Email | null) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: boolean) => void;
  setHasMoreEmails: (hasMore: boolean) => void;
  setTotalEmailsInFolder: (total: number) => void;
  setTotalPages: (pages: number) => void;
}

export const useEmailFetchOperations = ({
  emails,
  setEmails,
  setCurrentEmail,
  setLoading,
  setError,
  setHasMoreEmails,
  setTotalEmailsInFolder,
  setTotalPages,
}: UseEmailFetchOperationsProps) => {
  
  const requestsRef = useRef(new Set<string>());
  const pendingGetsRef = useRef(new Map<string, Promise<Email | null>>());
  const activeListRequestRef = useRef(0);

  // The backend detail endpoint marks the message as read server-side.
  // Mirror that in the list state and folder caches so the row shows read
  // when navigating back to the list — no extra request needed. Guarded to
  // avoid no-op state updates that would recreate callbacks in a loop.
  const syncListReadState = useCallback((id: string) => {
    emailCacheManager.updateEmailReadStatus(id, true);
    if (emails.some(e => e.id === id && !e.read)) {
      startTransition(() => {
        setEmails(emails.map(e => (e.id === id ? { ...e, read: true } : e)));
      });
    }
  }, [emails, setEmails]);

  const getEmail = useCallback(async (id: string, folder: string = 'inbox'): Promise<Email | null> => {
    const requestKey = `get-${folder}-${id}`;

    // Clear any previous global email error to prevent stale "Database Error" UI
    startTransition(() => {
      setError(false);
    });

    // Check email cache first
    const cachedEmail = emailCacheManager.getEmailCache(id);
    if (cachedEmail) {
      syncListReadState(id);
      startTransition(() => {
        setCurrentEmail(cachedEmail);
      });
      return cachedEmail;
    }

    // Folder list items are previews only. Recipients and snippets may be present,
    // but the full body is only returned by the folder-scoped detail endpoint.
    const existingEmail = emails.find(e => e.id === id);

    // Share the in-flight request: concurrent callers (e.g. detail-view effect
    // re-runs) await the same promise instead of receiving null, which the
    // detail view would misread as "email not found".
    const pendingRequest = pendingGetsRef.current.get(requestKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    const resolvedFolder = existingEmail?.folder || folder;

    const fetchPromise = (async (): Promise<Email | null> => {
      requestsRef.current.add(requestKey);
      startTransition(() => {
        setLoading(true);
      });

      try {
        const emailData = await fetchEmailById(id, resolvedFolder);
        const transformedEmail = transformFolderEmailDetailToEmail(emailData, resolvedFolder);

        // Compatibility fallback: the folder-scoped detail can come back with an
        // empty body/body_html, or with an empty attachments array even though
        // the list item flagged has_attachments. The legacy emailsys message
        // endpoint reads the same tables, so retry there before rendering.
        const bodyMissing = !transformedEmail.body && !transformedEmail.body_html;
        const attachmentsMissing =
          existingEmail?.has_attachments === true &&
          (!transformedEmail.attachments || transformedEmail.attachments.length === 0);

        if (bodyMissing || attachmentsMissing) {
          try {
            const legacy = await fetchLegacyEmailMessage(id);
            const legacyEmail = transformMessageDetailToEmail(legacy);
            if (bodyMissing && (legacyEmail.body || legacyEmail.body_html)) {
              transformedEmail.body = legacyEmail.body;
              transformedEmail.body_html = legacyEmail.body_html;
            }
            if ((!transformedEmail.attachments || transformedEmail.attachments.length === 0) && legacyEmail.attachments?.length) {
              transformedEmail.attachments = legacyEmail.attachments;
              transformedEmail.has_attachments = true;
            }
          } catch {
            // Legacy endpoint unavailable — render with what we have.
          }
        }

        emailCacheManager.setEmailCache(id, transformedEmail);
        syncListReadState(id);
        startTransition(() => {
          setCurrentEmail(transformedEmail);
          setLoading(false);
        });
        return transformedEmail;
      } catch (error: any) {
        // Check if it's an authentication error (401)
        const isAuthError = error?.status === 401 ||
                           error?.response?.status === 401 ||
                           error?.message?.includes('401') ||
                           error?.message?.includes('Invalid token') ||
                           error?.message?.includes('Unauthorized');

        // Check if it's a 404 Not Found error
        const isNotFoundError = error?.status === 404 ||
                               error?.response?.status === 404 ||
                               error?.message?.includes('404') ||
                               error?.message?.includes('not found');

        if (isAuthError) {
          console.error('Authentication error on email endpoint (401). Backend email router likely not using same JWT dependency/secret as /auth.');
          // Don't set error state for auth issues - just clear the email
          startTransition(() => {
            setCurrentEmail(null);
            setLoading(false);
          });
        } else if (isNotFoundError) {
          // Not found is normal if email was deleted or ID is invalid —
          // the detail view renders its own "Email Not Found" state.
          startTransition(() => {
            setCurrentEmail(null);
            setLoading(false);
          });
        } else {
          console.error('Failed to fetch email:', error?.message || error);
          startTransition(() => {
            setError(true);
            setCurrentEmail(null);
            setLoading(false);
          });
        }
        return null;
      } finally {
        requestsRef.current.delete(requestKey);
      }
    })();

    pendingGetsRef.current.set(requestKey, fetchPromise);
    try {
      return await fetchPromise;
    } finally {
      pendingGetsRef.current.delete(requestKey);
    }
  }, [emails, setCurrentEmail, setLoading, setError, syncListReadState]);

  const fetchEmails = useCallback(async (
    folder: string = 'inbox',
    limit: number = 25,
    page: number = 1,
    append: boolean = false,
    force: boolean = false,
    search?: string
  ): Promise<Email[]> => {
    const offset = (page - 1) * limit;
    const searchKey = search?.trim() || '';
    const requestKey = `fetch-${folder}-${limit}-${page}-${searchKey}-append-${append}`;
    // Check page-aware cache for non-append requests (forced refreshes skip it)
    if (!append && !force) {
      const cachedEmails = emailCacheManager.getFolderCache(folder, limit, offset, searchKey);
      if (cachedEmails) {
        startTransition(() => {
          setEmails(cachedEmails);
        });
        return cachedEmails;
      }
    }
    // Prevent duplicate requests within 1 second. Forced refreshes bypass this
    // guard: an in-flight request may have started before a mutation (sync,
    // delete, move) and must not cause the refresh to no-op. The
    // activeListRequestRef guard below still prevents stale responses from
    // overwriting newer state.
    if (!force && requestsRef.current.has(requestKey)) {
      return [];
    }

    try {
      const listRequestId = ++activeListRequestRef.current;
      startTransition(() => {
        setError(false);
      });

      requestsRef.current.add(requestKey);

      startTransition(() => {
        setLoading(true);
      });

      const response = await fetchEmailsInFolder(folder, limit, page, undefined, searchKey || undefined);
      if (listRequestId !== activeListRequestRef.current) {
        return [];
      }

      const emailsData = (response.emails || []).map((item) => transformFolderEmailToEmail(item, folder));

      // Handle append mode for "load more" functionality
      const finalEmailsData = append ? [...emails, ...emailsData] : emailsData;

      // Use backend-provided pagination metadata directly
      const totalCount = response.total_count || 0;
      const totalPages = Math.max(1, Math.ceil(totalCount / limit));
      const hasMore = page < totalPages;

      // Cache the results for this specific page
      emailCacheManager.setFolderCache(folder, finalEmailsData, limit, offset, searchKey);

      startTransition(() => {
        setEmails(finalEmailsData);
        setHasMoreEmails(hasMore);
        setTotalEmailsInFolder(totalCount);
        setTotalPages(totalPages);
        setLoading(false);
      });
      return finalEmailsData;
    } catch (error: any) {
      // Check if it's an authentication error (401)
      const isAuthError = error?.status === 401 ||
                         error?.response?.status === 401 ||
                         error?.message?.includes('401') ||
                         error?.message?.includes('Invalid token') ||
                         error?.message?.includes('Unauthorized');

      if (isAuthError) {
        console.error('Authentication error on email endpoint (401). Backend email router likely not using same JWT dependency/secret as /auth.');
        // Don't set error state for auth issues - just show empty list
        startTransition(() => {
          setEmails([]);
          setLoading(false);
        });
      } else {
        console.error('Failed to fetch emails:', error?.message || error);
        startTransition(() => {
          setError(true);
          setEmails([]);
          setLoading(false);
        });
      }
      return [];
    } finally {
      requestsRef.current.delete(requestKey);
    }
  }, [emails, setEmails, setLoading, setError, setHasMoreEmails, setTotalEmailsInFolder, setTotalPages]);

  return {
    fetchEmails,
    getEmail,
  };
};
